import { apiService } from './api';
import { secureTokenStorage } from './secureTokenStorage';
import { csrfService } from './csrfService';
import { secureLogger } from '@/utils/secureLogger';
import type { 
  AuthResponse, 
  LoginCredentials, 
  RegisterData, 
  User
} from '@/types';

export class AuthService {
  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // SECURITY: Ensure CSRF token is available before login
    await csrfService.ensureCSRFToken();
    
    const response = await apiService.post<AuthResponse>('/auth/login', credentials);
    
    if (response.success && response.data) {
      // SECURITY: Store access token in memory only, refresh token in HttpOnly cookie
      secureTokenStorage.setAccessToken(
        response.data.access_token, 
        response.data.expires_in
      );
      
      // Store user info and permissions in localStorage (non-sensitive data)
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
      
      secureLogger.debug('🔐 Login successful - tokens stored securely');
      return response.data;
    }
    
    throw new Error(response.message || 'Login failed');
  }

  public async register(data: RegisterData): Promise<AuthResponse> {
    // SECURITY: Ensure CSRF token is available before registration
    await csrfService.ensureCSRFToken();
    
    const response = await apiService.post<AuthResponse>('/auth/register', data);
    
    if (response.success && response.data) {
      // SECURITY: Store access token in memory only
      secureTokenStorage.setAccessToken(
        response.data.access_token, 
        response.data.expires_in
      );
      
      // Store user info and permissions in localStorage (non-sensitive data)
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
      
      return response.data;
    }
    
    throw new Error(response.message || 'Registration failed');
  }

  public async logout(): Promise<void> {
    try {
      // Use secure token storage logout (handles HttpOnly cookie cleanup)
      await secureTokenStorage.logout();
    } catch (error) {
      secureLogger.warn('Logout request failed:', error);
    }
    
    // Clear non-sensitive localStorage data
    this.clearUserData();
  }

  public async logoutAll(): Promise<void> {
    try {
      await apiService.post('/auth/logout-all');
    } catch (error) {
      secureLogger.warn('Logout all request failed:', error);
    }
    
    // Clear all data
    secureTokenStorage.clearTokens();
    this.clearUserData();
  }

  public async getProfile(): Promise<User> {
    const response = await apiService.get<{ 
      user: User; 
      permissions: string[]; 
      active_sessions: number; 
    }>('/auth/profile');
    
    if (response.success && response.data) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
      return response.data.user;
    }
    
    throw new Error(response.message || 'Failed to fetch profile');
  }

  public async updateProfile(data: { name?: string; email?: string }): Promise<User> {
    const response = await apiService.put<{ user: User }>('/auth/profile', data);
    
    if (response.success && response.data) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data.user;
    }
    
    throw new Error(response.message || 'Failed to update profile');
  }

  public async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    const response = await apiService.post('/auth/change-password', data);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to change password');
    }
  }

  public async requestPasswordReset(email: string): Promise<void> {
    const response = await apiService.post('/auth/password-reset', { email });
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to request password reset');
    }
  }

  public async resetPassword(token: string, password: string): Promise<void> {
    const response = await apiService.post('/auth/password-reset/confirm', {
      token,
      password,
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to reset password');
    }
  }

  public async verifyEmail(token: string): Promise<void> {
    const response = await apiService.get(`/auth/verify-email/${token}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to verify email');
    }
  }

  public getCurrentUser(): User | null {
    try {
      const userJson = localStorage.getItem('user');
      return userJson ? JSON.parse(userJson) : null;
    } catch (error) {
      secureLogger.error('Error parsing user data:', error);
      return null;
    }
  }

  public getUserPermissions(): string[] {
    try {
      const permissionsJson = localStorage.getItem('permissions');
      return permissionsJson ? JSON.parse(permissionsJson) : [];
    } catch (error) {
      secureLogger.error('Error parsing permissions data:', error);
      return [];
    }
  }

  public hasPermission(permission: string): boolean {
    const permissions = this.getUserPermissions();
    
    // Admin has all permissions
    if (permissions.includes('*')) {
      return true;
    }
    
    // Direct permission match
    if (permissions.includes(permission)) {
      return true;
    }
    
    // Wildcard permission match (e.g., 'users:*' matches 'users:read')
    const [resource] = permission.split(':');
    const wildcardPermission = `${resource}:*`;
    if (permissions.includes(wildcardPermission)) {
      return true;
    }
    
    return false;
  }

  public hasRole(role: string | string[]): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(user.role);
  }

  public async isAuthenticated(): Promise<boolean> {
    return await secureTokenStorage.isAuthenticated() && this.getCurrentUser() !== null;
  }

  // DEPRECATED: These methods are kept for backward compatibility but should not be used
  // They now delegate to secure token storage
  public getAccessToken(): string | null {
    secureLogger.warn('⚠️ getAccessToken() is deprecated. Tokens are now managed securely.');
    return secureTokenStorage.getCurrentToken();
  }

  public getRefreshToken(): string | null {
    secureLogger.warn('⚠️ getRefreshToken() is deprecated. Refresh tokens are now in HttpOnly cookies.');
    return null; // Never expose refresh tokens
  }

  private clearUserData(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    secureLogger.debug('🧹 User data cleared from localStorage');
  }

  public async refreshProfile(): Promise<User> {
    try {
      return await this.getProfile();
    } catch (error) {
      // If profile refresh fails, clear user data and redirect to login
      this.clearUserData();
      secureTokenStorage.clearTokens();
      throw error;
    }
  }

  public isTokenExpired(): boolean {
    return secureTokenStorage.isTokenExpired();
  }

  public getTokenExpirationTime(): Date | null {
    return secureTokenStorage.getTokenExpiration();
  }
}

export const authService = new AuthService();
export default authService;